import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { QuickFilterKey, QuickFilterListItem } from './types';

type Props = {
  list: QuickFilterListItem[];
  onSelect: (filter: QuickFilterKey) => void;
};

export const QuickFilterDropdown = ({ list, onSelect }: Props) => {
  const { isOpen, close, getTriggerProps, getDialogProps } = usePopoverDialog<HTMLDivElement>({ placement: 'bottom' });
  const totalCount = list.reduce((acc, item) => acc + (item.key === QuickFilterKey.All ? 0 : item.count), 0);

  return (
    <div css={styles.root(isOpen)} {...getTriggerProps()}>
      <div css={styles.countContainer}>
        <Icon name='schedule-pulse' size={16} css={{ flexShrink: 0 }} />
        {totalCount > 0 && (
          <Text size='small' weight='bold' color='white' css={styles.countBadge}>
            {totalCount}
          </Text>
        )}
      </div>
      <div className='summary-dropdown-container' css={styles.dropdownContainer}>
        <Icon name='caret-down' size={8} css={{ flexShrink: 0 }} />
      </div>
      <PopoverDialog {...getDialogProps()} css={styles.popoverContainer}>
        {list.map((item) => (
          <div
            key={item.label}
            css={styles.popoverOption}
            onClick={() => {
              onSelect(item.key);
              close();
            }}
          >
            <Icon name={item.iconName} size={16} color='subdued' css={{ flexShrink: 0 }} />
            <Text>{item.count}</Text>
            <Text>{item.label}</Text>
          </div>
        ))}
      </PopoverDialog>
    </div>
  );
};

const styles = {
  root: (isDropdownOpen: boolean) => css`
    display: flex;
    border: 1px solid ${theme.colors.neutral30};
    border-radius: ${theme.borderRadius.small};
    cursor: pointer;

    .summary-dropdown-container {
      background: ${isDropdownOpen ? theme.colors.neutral10 : 'transparent'};
    }

    &:hover {
      .summary-dropdown-container {
        background: ${isDropdownOpen ? theme.colors.neutral10 : theme.colors.neutral5};
      }
    }
  `,
  countContainer: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(1, 1.5)};
    border-right: 1px solid ${theme.colors.neutral30};
  `,
  countBadge: css`
    background-color: ${theme.colors.critical50};
    border-radius: ${theme.borderRadius.full};
    padding: ${theme.spacing(0, 0.5)};
  `,
  dropdownContainer: css`
    padding: ${theme.spacing(0, 0.75)};
    display: flex;
    align-items: center;
    border-radius: 0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0;
    & > svg {
      align-self: center;
    }
  `,
  popoverContainer: css`
    padding: 0;
    border-radius: ${theme.borderRadius.small};
    box-shadow: ${theme.shadows.floating};
  `,
  popoverOption: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
    cursor: pointer;
    &:hover {
      background: ${theme.colors.neutral5};
    }
  `,
};
